import type { ProviderConnection } from '@bmai/connections-api-client';
import type { ConnectionTestCheck, ProviderAuthMethod, ProviderConnectionId, ProviderType } from '@bmai/shared';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IntegrationsService, type ConnectionIngestionStatus, type ProviderOption } from './IntegrationsService';
import { openOauthPopup, type OauthPopupHandle } from './OauthPopupHelper';
import { openInstallPopup, type InstallPopupHandle } from './InstallPopupHelper';
import type { InstallFlowDescriptor, ProviderTypeWithInstallFlow } from './InstallFlowTypes';
import { resolveDisplayNameTemplate } from './wizard/resolveDisplayNameTemplate';

interface BuildInstallUrlResponse {
    installUrl: string;
    state: string;
}

interface ResolveResponse {
    connectionConfig: Record<string, unknown>;
}

export function getInstallFlowDescriptor(
    providerType: ProviderType | null | undefined,
): InstallFlowDescriptor | undefined {
    if (!providerType) return undefined;
    return (providerType as ProviderTypeWithInstallFlow).installFlow;
}

export interface IntegrationFormState {
    providerTypeId: string | null;
    authMethod: ProviderAuthMethod | null;
    config: Record<string, unknown>;
    enabled: boolean;
    displayName: string;
}

export interface IntegrationSaveResult {
    ok: boolean;
    editing: boolean;
    providerDisplayName: string;
    error?: string;
    /**
     * Set by `startInstallFlow` when the install popup closed without a
     * confirmable success but a manual fallback is available. The
     * Layout treats this as a soft signal — switching to fallback mode
     * with a friendly prompt — rather than surfacing a hard error. This
     * is the primary recovery path for vendors (e.g. GitHub on
     * restricted orgs) that don't always fire the Setup-URL redirect.
     */
    suggestFallback?: boolean;
    /**
     * Provider error subcode (e.g. Microsoft's `error_subcode=cancel`) when
     * the OAuth flow returns an error. Layout uses this to offer follow-up
     * actions like "Request admin approval" or "Check again".
     */
    errorSubcode?: string;
}

export interface IntegrationValidationState {
    running: boolean;
    result: {
        valid: boolean;
        message: string | null;
        checks?: ConnectionTestCheck[];
        sampleRecord?: string;
    } | null;
}

const EMPTY_FORM: IntegrationFormState = {
    providerTypeId: null,
    authMethod: null,
    config: {},
    enabled: true,
    displayName: '',
};

export abstract class IntegrationsPageViewModel extends BaseViewModel {
    providerTypes: ProviderType[] = [];
    connections: ProviderConnection[] = [];
    ingestionStatus: Map<string, ConnectionIngestionStatus> = new Map();
    loading: boolean = true;
    loadError: string | null = null;

    modalOpen: boolean = false;
    editing: boolean = false;
    editingId: string | null = null;
    form: IntegrationFormState = { ...EMPTY_FORM, config: {} };
    saving: boolean = false;
    validation: IntegrationValidationState = { running: false, result: null };
    private activeOauthHandle: OauthPopupHandle | null = null;
    private activeInstallHandle: InstallPopupHandle | null = null;

    constructor(
        protected service: IntegrationsService,
        protected interfaceName: string,
    ) {
        super();
    }

    statusFor(connectionId: string): ConnectionIngestionStatus | undefined {
        return this.ingestionStatus.get(connectionId);
    }

    /**
     * Per-connection health feed for the cards. Defaults to the telemetry
     * ingestion feed (AI-usage providers); directory pages override this to
     * read the directory-sync feed instead.
     */
    protected fetchConnectionStatus(): Promise<ConnectionIngestionStatus[]> {
        return this.service.fetchIngestionStatus();
    }

    get selectedProviderType(): ProviderType | null {
        if (!this.form.providerTypeId) return null;
        return this.providerTypes.find((p) => p.id === this.form.providerTypeId) ?? null;
    }

    /**
     * Read a single string-typed config field from the in-progress form draft.
     * Returns undefined when the field is unset or stored as a non-string.
     * Use {@link setFieldValue} to write — this is the read counterpart that lets
     * scoped child VMs avoid reaching into `form.config` directly.
     */
    getConfigString(name: string): string | undefined {
        const v = this.form.config[name];
        return typeof v === 'string' ? v : undefined;
    }

    async load(): Promise<void> {
        this.loading = true;
        this.loadError = null;
        this.notifyChange();
        const [typesResult, connsResult, statusResult] = await Promise.allSettled([
            this.service.listProviderTypes(this.interfaceName),
            this.service.listConnections(),
            this.fetchConnectionStatus(),
        ]);
        const errors: string[] = [];
        if (typesResult.status === 'fulfilled') {
            this.providerTypes = typesResult.value;
        } else {
            errors.push(`provider types: ${(typesResult.reason as Error).message}`);
        }
        if (connsResult.status === 'fulfilled') {
            const typeIds = new Set(this.providerTypes.map((t) => t.id));
            this.connections = connsResult.value.filter((c) => typeIds.has(c.providerTypeId));
        } else {
            errors.push(`connections: ${(connsResult.reason as Error).message}`);
        }
        // Connection status is best-effort: the list renders without it, so a
        // failure just leaves every card in its "pending" (no-status) state.
        this.ingestionStatus = statusResult.status === 'fulfilled'
            ? new Map(statusResult.value.map((s) => [s.providerConnectionId, s]))
            : new Map();
        this.loadError = errors.length > 0 ? errors.join('; ') : null;
        this.loading = false;
        this.notifyChange();
    }

    openCreate(): void {
        this.form = { ...EMPTY_FORM, config: {} };
        this.editing = false;
        this.editingId = null;
        this.validation = { running: false, result: null };
        this.modalOpen = true;
        this.notifyChange();
    }

    openEdit(conn: ProviderConnection): void {
        const type = this.providerTypes.find((p) => p.id === conn.providerTypeId);
        const storedAuth = (conn.config as { authMethod?: ProviderAuthMethod }).authMethod;
        const authMethod = storedAuth ?? type?.authTypes[0]?.method ?? null;
        this.form = {
            providerTypeId: conn.providerTypeId,
            authMethod,
            config: { ...(conn.config as Record<string, unknown>) },
            enabled: conn.enabled,
            displayName: conn.displayName ?? '',
        };
        this.editing = true;
        this.editingId = conn.id;
        this.validation = { running: false, result: null };
        this.modalOpen = true;
        this.notifyChange();
    }

    setDisplayName(value: string): void {
        this.form = { ...this.form, displayName: value };
        this.notifyChange();
    }

    closeModal(): void {
        this.cancelActiveOauth();
        this.cancelActiveInstall();
        this.modalOpen = false;
        this.notifyChange();
    }

    override dispose(): void {
        this.cancelActiveOauth();
        this.cancelActiveInstall();
        super.dispose();
    }

    private cancelActiveOauth(): void {
        if (this.activeOauthHandle) {
            this.activeOauthHandle.close();
            this.activeOauthHandle = null;
        }
    }

    private cancelActiveInstall(): void {
        if (this.activeInstallHandle) {
            this.activeInstallHandle.close();
            this.activeInstallHandle = null;
        }
    }

    selectProviderType(providerTypeId: string): void {
        const type = this.providerTypes.find((p) => p.id === providerTypeId) ?? null;
        const authMethod = type?.authTypes[0]?.method ?? null;
        this.form = {
            providerTypeId,
            authMethod,
            config: {},
            enabled: true,
            displayName: '',
        };
        this.validation = { running: false, result: null };
        this.notifyChange();
    }

    setAuthMethod(method: ProviderAuthMethod): void {
        this.form = { ...this.form, authMethod: method, config: {} };
        this.validation = { running: false, result: null };
        this.notifyChange();
    }

    setFieldValue(name: string, value: unknown): void {
        this.form = { ...this.form, config: { ...this.form.config, [name]: value } };
        this.validation = { running: false, result: null };
        this.notifyChange();
    }

    setEnabled(enabled: boolean): void {
        this.form = { ...this.form, enabled };
        this.notifyChange();
    }

    private buildConfig(): Record<string, unknown> {
        const { authMethod, config } = this.form;
        return authMethod ? { authMethod, ...config } : { ...config };
    }

    /**
     * Run a validation against the current form. In edit mode this targets the saved
     * connection so the backend can merge stored secrets (which are not re-typed in
     * the form) with the supplied config; in create mode it validates the form's
     * config directly.
     */
    async validate(): Promise<void> {
        if (!this.form.providerTypeId) return;
        this.validation = { running: true, result: null };
        this.notifyChange();
        try {
            const result = this.editing && this.editingId
                ? await this.service.validateConnection(this.editingId)
                : await this.service.validateConfig(this.form.providerTypeId, this.buildConfig());
            this.validation = { running: false, result };
        } catch (e) {
            this.validation = { running: false, result: { valid: false, message: (e as Error).message } };
        } finally {
            this.notifyChange();
        }
    }

    async save(): Promise<IntegrationSaveResult> {
        const type = this.selectedProviderType;
        if (!type || !this.form.providerTypeId) {
            return { ok: false, editing: this.editing, providerDisplayName: '', error: 'Select a provider type' };
        }
        const wasEditing = this.editing;
        this.saving = true;
        this.notifyChange();
        try {
            const config = this.buildConfig();
            const displayName = this.resolveDisplayName(type, config);
            if (wasEditing && this.editingId) {
                const update: { config: Record<string, unknown>; enabled: boolean; displayName: string | null } = {
                    config,
                    enabled: this.form.enabled,
                    displayName: displayName ?? null,
                };
                await this.service.updateConnection(this.editingId, update);
            } else {
                await this.service.createConnection({
                    providerTypeId: this.form.providerTypeId,
                    config,
                    enabled: this.form.enabled,
                    ...(displayName !== undefined ? { displayName } : {}),
                });
            }
            this.modalOpen = false;
            this.form = { ...EMPTY_FORM, config: {} };
            void this.load();
            return { ok: true, editing: wasEditing, providerDisplayName: type.displayName };
        } catch (e) {
            return { ok: false, editing: wasEditing, providerDisplayName: type.displayName, error: (e as Error).message };
        } finally {
            this.saving = false;
            this.notifyChange();
        }
    }

    private resolveDisplayName(type: ProviderType, config: Record<string, unknown>): string | undefined {
        const trimmed = this.form.displayName.trim();
        if (trimmed) return trimmed;
        if (!type.displayNameTemplate) return undefined;
        const resolved = resolveDisplayNameTemplate(type.displayNameTemplate, config);
        return resolved ?? undefined;
    }

    async startOauth(): Promise<IntegrationSaveResult> {
        const type = this.selectedProviderType;
        if (!type || !this.form.providerTypeId) {
            return { ok: false, editing: false, providerDisplayName: '', error: 'Select a provider type' };
        }
        const providerTypeId = this.form.providerTypeId;
        this.saving = true;
        this.notifyChange();
        try {
            const url = await this.service.startOauth({
                providerTypeId,
                redirectUri: this.service.oauthCallbackUrl(),
                connectionConfig: this.buildConfig(),
            });
            const handle = openOauthPopup(url);
            this.activeOauthHandle = handle;
            const result = await handle.result;
            this.activeOauthHandle = null;
            if (result.cancelled) {
                return { ok: false, editing: false, providerDisplayName: type.displayName };
            }
            if (!result.ok || !result.code || !result.state) {
                const out: IntegrationSaveResult = {
                    ok: false,
                    editing: false,
                    providerDisplayName: type.displayName,
                };
                if (result.error !== undefined) out.error = result.error;
                if (result.errorSubcode !== undefined) out.errorSubcode = result.errorSubcode;
                return out;
            }
            const callback = await this.service.completeOauth({ code: result.code, state: result.state });

            if (callback.connectionConfig) {
                this.form = {
                    ...this.form,
                    config: { ...this.form.config, ...callback.connectionConfig },
                };
            }
            return { ok: true, editing: false, providerDisplayName: type.displayName };
        } catch (e) {
            return { ok: false, editing: false, providerDisplayName: type.displayName, error: (e as Error).message };
        } finally {
            this.activeOauthHandle = null;
            this.saving = false;
            this.notifyChange();
        }
    }

    /**
     * Re-run the OAuth grant for an existing connection. The backend's
     * callback patches just the refresh token (and any provider-handler
     * additionalConnectionConfig like Microsoft id_token claims) into the
     * existing connection — all other fields (matterIds, accountIds,
     * region, etc.) are preserved.
     *
     * Powers the Re-authenticate button on the connection card and the
     * wizard's edit-auth step.
     */
    async reauthenticate(connectionId: ProviderConnectionId): Promise<IntegrationSaveResult> {
        const conn = this.connections.find((c) => c.id === connectionId);
        const type = conn ? this.providerTypes.find((p) => p.id === conn.providerTypeId) : undefined;
        if (!conn || !type) {
            return { ok: false, editing: true, providerDisplayName: '', error: 'Connection not found' };
        }
        this.saving = true;
        this.notifyChange();
        try {
            const url = await this.service.startReauth(conn.id as string, {
                redirectUri: this.service.oauthCallbackUrl(),
            });
            const handle = openOauthPopup(url);
            this.activeOauthHandle = handle;
            const result = await handle.result;
            this.activeOauthHandle = null;
            if (result.cancelled) {
                return { ok: false, editing: true, providerDisplayName: type.displayName };
            }
            if (!result.ok || !result.code || !result.state) {
                const out: IntegrationSaveResult = {
                    ok: false,
                    editing: true,
                    providerDisplayName: type.displayName,
                };
                if (result.error !== undefined) out.error = result.error;
                if (result.errorSubcode !== undefined) out.errorSubcode = result.errorSubcode;
                return out;
            }
            await this.service.completeOauth({ code: result.code, state: result.state });
            await this.load();
            return { ok: true, editing: true, providerDisplayName: type.displayName };
        } catch (e) {
            return { ok: false, editing: true, providerDisplayName: type.displayName, error: (e as Error).message };
        } finally {
            this.activeOauthHandle = null;
            this.saving = false;
            this.notifyChange();
        }
    }

    /**
     * Run the Microsoft Entra `/adminconsent` flow to grant tenant-wide
     * consent. Used when the regular `startOauth` returns
     * `error_subcode=cancel` (typically because admin consent hasn't been
     * granted yet).
     *
     * Result `ok=true` means consent was granted at the tenant level; the
     * caller should prompt the user to click Connect to actually create the
     * connection. We deliberately do NOT auto-chain into a second
     * /authorize popup — the regular flow with cached consent is fast
     * enough on the next click and avoids surprising the user with a
     * second popup window.
     */
    async requestAdminConsent(): Promise<IntegrationSaveResult> {
        const type = this.selectedProviderType;
        if (!type || !this.form.providerTypeId) {
            return { ok: false, editing: false, providerDisplayName: '', error: 'Select a provider type' };
        }
        const providerTypeId = this.form.providerTypeId;
        this.saving = true;
        this.notifyChange();
        try {
            const url = await this.service.startAdminConsentOauth({
                providerTypeId,
                redirectUri: this.service.oauthCallbackUrl(),
                connectionConfig: this.buildConfig(),
            });
            const handle = openOauthPopup(url);
            this.activeOauthHandle = handle;
            const result = await handle.result;
            this.activeOauthHandle = null;
            if (result.cancelled) {
                return { ok: false, editing: false, providerDisplayName: type.displayName };
            }
            if (!result.ok || !result.adminConsentGranted) {
                const out: IntegrationSaveResult = {
                    ok: false,
                    editing: false,
                    providerDisplayName: type.displayName,
                };
                if (result.error !== undefined) out.error = result.error;
                if (result.errorSubcode !== undefined) out.errorSubcode = result.errorSubcode;
                return out;
            }
            return { ok: true, editing: false, providerDisplayName: type.displayName };
        } catch (e) {
            return { ok: false, editing: false, providerDisplayName: type.displayName, error: (e as Error).message };
        } finally {
            this.activeOauthHandle = null;
            this.saving = false;
            this.notifyChange();
        }
    }

    async startInstallFlow(): Promise<IntegrationSaveResult> {
        const type = this.selectedProviderType;
        if (!type || !this.form.providerTypeId) {
            return { ok: false, editing: false, providerDisplayName: '', error: 'Select a provider type' };
        }
        const flow = getInstallFlowDescriptor(type);
        if (!flow) {
            return { ok: false, editing: false, providerDisplayName: type.displayName, error: 'No install flow declared for this provider.' };
        }
        const providerTypeId = this.form.providerTypeId;
        this.saving = true;
        this.notifyChange();
        try {
            const built = await this.service.callProviderTypeRoute<BuildInstallUrlResponse>(
                providerTypeId,
                flow.popup.buildUrlRoute,
                {},
            );
            const handle = openInstallPopup(built.installUrl);
            this.activeInstallHandle = handle;
            const popupResult = await handle.result;
            this.activeInstallHandle = null;
            const fallbackAvailable = !!flow.fallback;
            if (popupResult.cancelled) {
                return {
                    ok: false,
                    editing: false,
                    providerDisplayName: type.displayName,
                    suggestFallback: fallbackAvailable,
                };
            }
            if (!popupResult.ok || !popupResult.params) {
                return {
                    ok: false,
                    editing: false,
                    providerDisplayName: type.displayName,
                    error: popupResult.error,
                    suggestFallback: fallbackAvailable,
                };
            }
            if (popupResult.params['state'] !== built.state) {
                return {
                    ok: false,
                    editing: false,
                    providerDisplayName: type.displayName,
                    error: 'Install state mismatch — possible CSRF, aborting.',
                };
            }
            const resolved = await this.service.callProviderTypeRoute<ResolveResponse>(
                providerTypeId,
                flow.popup.resolveRoute,
                popupResult.params,
            );
            this.form = {
                ...this.form,
                config: { ...this.form.config, ...resolved.connectionConfig },
            };
            return { ok: true, editing: false, providerDisplayName: type.displayName };
        } catch (e) {
            return { ok: false, editing: false, providerDisplayName: type.displayName, error: (e as Error).message };
        } finally {
            this.activeInstallHandle = null;
            this.saving = false;
            this.notifyChange();
        }
    }

    async createConnectionFromInstallFallback(value: string): Promise<IntegrationSaveResult> {
        const type = this.selectedProviderType;
        if (!type || !this.form.providerTypeId) {
            return { ok: false, editing: false, providerDisplayName: '', error: 'Select a provider type' };
        }
        const flow = getInstallFlowDescriptor(type);
        if (!flow?.fallback) {
            return { ok: false, editing: false, providerDisplayName: type.displayName, error: 'This provider does not declare a fallback install path.' };
        }
        const trimmed = value.trim();
        if (!trimmed) {
            return { ok: false, editing: false, providerDisplayName: type.displayName, error: `Enter the ${flow.fallback.fieldLabel}.` };
        }
        const providerTypeId = this.form.providerTypeId;
        this.saving = true;
        this.notifyChange();
        try {
            const resolved = await this.service.callProviderTypeRoute<ResolveResponse>(
                providerTypeId,
                flow.fallback.resolveRoute,
                { [flow.fallback.fieldName]: trimmed },
            );
            this.form = {
                ...this.form,
                config: { ...this.form.config, ...resolved.connectionConfig },
            };
            return { ok: true, editing: false, providerDisplayName: type.displayName };
        } catch (e) {
            return { ok: false, editing: false, providerDisplayName: type.displayName, error: (e as Error).message };
        } finally {
            this.saving = false;
            this.notifyChange();
        }
    }

    async fetchFieldOptions(route: string): Promise<ProviderOption[]> {
        if (!this.form.providerTypeId) return [];
        return this.service.callbackProviderType(this.form.providerTypeId, route, {
            connectionId: this.editingId,
            providerTypeId: this.form.providerTypeId,
            config: this.buildConfig(),
            enabled: this.form.enabled,
        });
    }

    /**
     * Fetch a server-minted tenant-secret value via a provider-callback route.
     * Idempotent on the backend — same value returned for the lifetime of the
     * (tenant, providerType, field) triple. Used by wizard fields declared with
     * `tenantSecret` (AWS External ID).
     */
    async fetchTenantSecret(providerTypeId: string, route: string): Promise<{ value: string }> {
        return this.service.callProviderTypeRoute<{ value: string }>(providerTypeId, route, {});
    }

    /**
     * Call a provider-callback route with the current connection config draft.
     * Used by wizard auth-step `launchActions` (e.g. AWS CloudFormation
     * quick-create URL builder).
     */
    async callProviderRoute(providerTypeId: string, route: string, body: Record<string, unknown>): Promise<unknown> {
        return this.service.callProviderTypeRoute<unknown>(providerTypeId, route, body);
    }

    async deleteConnection(id: string): Promise<{ ok: boolean; error?: string }> {
        try {
            await this.service.deleteConnection(id);
            void this.load();
            return { ok: true };
        } catch (e) {
            return { ok: false, error: (e as Error).message };
        }
    }
}
